import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { AccommodationType } from "@shared/models/article";
import SortSvg from "../../../assets/svg/chevron-sort.svg";
import "./search-sort.scss";

const values: Record<string, any> = {
  "name-asc": { title: "ASC" },
  "name-desc": { title: "DESC" },
  "price-asc": { price: "ASC" },
  "price-desc": { price: "DESC" },
  "guests-asc": { guests: "ASC" },
  "guests-desc": { guests: "DESC" },
  "rooms-asc": { rooms: "ASC" },
  "rooms-desc": { rooms: "DESC" },
  "surface-asc": { surface: "ASC" },
  "surface-desc": { surface: "DESC" },
}

type SearchSortProps = {
  type: AccommodationType,
  data?: any,
  onChange?: (value?: any) => void
}

const SearchSort = (
  {
    type,
    data,
    onChange = () => void 0
  }: SearchSortProps) => {

  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const activatorRef = useRef<HTMLInputElement>(null);
  const [ visible, setVisible ] = useState<boolean>(false);

  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setVisible(false);
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside)
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    }
  }, []);

  const value = useMemo(() => {
    const index = Object.values(values).findIndex(value => value === data);
    return index >= 0 ? Object.keys(values)[index] : undefined;
  }, [ data ]);

  const label = useCallback((val?: string) => {
    switch (val) {
      case "name-asc":
        return intl.formatMessage({ id: "search-sort-name", defaultMessage: "Nom (ordre croissant)" });
      case "name-desc":
        return intl.formatMessage({ id: "search-sort-name-desc", defaultMessage: "Nom (ordre décroissant)" });
      case "price-asc":
        return intl.formatMessage({ id: "search-sort-price", defaultMessage: "Prix (le - cher en premier)" });
      case "price-desc":
        return intl.formatMessage({ id: "search-sort-price-desc", defaultMessage: "Prix (le + cher en premier)" });
      case "guests-asc":
        return intl.formatMessage({ id: "search-sort-guests", defaultMessage: "Personnes (ordre croissant)" });
      case "guests-desc":
        return intl.formatMessage({ id: "search-sort-guests-desc", defaultMessage: "Personnes (ordre décroissant)" });
      case "rooms-asc":
        return intl.formatMessage({ id: "search-sort-rooms", defaultMessage: "Pièces (order croissant)" });
      case "rooms-desc":
        return intl.formatMessage({ id: "search-sort-rooms-desc", defaultMessage: "Pièces (ordre décroissant)" });
      case "surface-asc":
        return intl.formatMessage({ id: "search-sort-surface", defaultMessage: "Surface (order croissant)" });
      case "surface-desc":
        return intl.formatMessage({ id: "search-sort-surface-desc", defaultMessage: "Surface (order décroissant)" });
      default:
        return intl.formatMessage({ id: "search-sort-default", defaultMessage: "Défaut" });
    }
  }, [ intl ]);

  return (
    <div ref={containerRef}
         className="sort-container"
         onClick={() => setVisible(prev => !prev)}>
      <input ref={activatorRef} type="checkbox" checked={visible} readOnly={true} />
      {`${intl.formatMessage({ id: "search-sort-label", defaultMessage: "Trier par"})}:`}
      <span className="value">{label(value)}</span>
      <SortSvg />
      <ul className="sort-selector">
        <li onClick={() => onChange()}>{label()}</li>
        <li onClick={() => onChange(values["name-asc"])}>{label("name-asc")}</li>
        <li onClick={() => onChange(values["name-desc"])}>{label("name-desc")}</li>
        <li onClick={() => onChange(values["price-asc"])}>{label("price-asc")}</li>
        <li onClick={() => onChange(values["price-desc"])}>{label("price-desc")}</li>
        {[ AccommodationType.RENTAL, AccommodationType.SEASONAL].includes(type) &&
          <>
            <li onClick={() => onChange(values["guests-asc"])}>{label("guests-asc")}</li>
            <li onClick={() => onChange(values["guests-desc"])}>{label("guests-desc")}</li>
          </>
        }
        {[ AccommodationType.ANNUAL, AccommodationType.SALE ].includes(type) &&
          <>
            <li onClick={() => onChange(values["rooms-asc"])}>{label("rooms-asc")}</li>
            <li onClick={() => onChange(values["rooms-desc"])}>{label("rooms-desc")}</li>
            <li onClick={() => onChange(values["surface-asc"])}>{label("surface-asc")}</li>
            <li onClick={() => onChange(values["surface-desc"])}>{label("surface-desc")}</li>
          </>
        }
      </ul>
    </div>);

};

export default SearchSort;
